import { Link } from "react-router-dom";
import { GraduationCap, ShieldCheck, ArrowRight, BookOpenCheck, FileSpreadsheet, FileText, CheckCircle2, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const heroImg = "/hero-sekolah.jpg";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50/60 bg-grid" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-sekolah.png" alt="Logo SMPN 1 Penengahan" className="w-11 h-11 object-contain" />
            <div>
              <div className="font-display font-bold text-slate-900 leading-none text-sm">SMP NEGERI 1 PENENGAHAN</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mt-0.5">Buku Induk Digital • Merdeka Belajar</div>
            </div>
          </div>
          <Link to="/admin/login">
            <Button variant="ghost" className="text-slate-700 hover:text-slate-900" data-testid="header-admin-login-link">
              Masuk Admin
              <ShieldCheck className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 font-display leading-[1.05]">
            <span className="text-blue-700">Buku Induk Digital</span> Siswa SMPN 1 Penengahan
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            Digitalisasi biodata siswa, sinkronisasi otomatis nilai dari Leger, hingga cetak Buku Induk resmi 7+ halaman lengkap dengan keterangan kelulusan.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/siswa">
              <Button size="lg" className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-6 h-12" data-testid="cta-siswa-button">
                <GraduationCap className="w-5 h-5 mr-2" />
                Portal Siswa
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/admin/login">
              <Button size="lg" variant="outline" className="border-slate-300 rounded-lg px-6 h-12 hover:bg-slate-100" data-testid="cta-admin-button">
                <ShieldCheck className="w-5 h-5 mr-2" />
                Masuk Admin TU
              </Button>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            <Stat number="7+" label="Halaman/Siswa" />
            <Stat number="6" label="Semester Nilai" />
            <Stat number="Lulus/Tidak" label="Keterangan Kelulusan" />
          </div>
        </div>

        <div className="relative">
          <img
            src={heroImg}
            alt="Siswa dan Guru SMPN 1 Penengahan"
            className="relative rounded-2xl shadow-xl object-cover w-full h-[440px] border border-slate-200"
          />
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3">Fitur Unggulan</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight">
              Dari input data hingga surat kelulusan
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Feature
              icon={<GraduationCap className="w-6 h-6" />}
              title="Portal Siswa Publik"
              desc="Siswa isi biodata Merdeka Belajar & unggah KK, Ijazah SD, Akta Kelahiran, Pas Foto 3x4 — tanpa perlu login."
            />
            <Feature
              icon={<FileSpreadsheet className="w-6 h-6" />}
              title="Impor Leger Excel"
              desc="Sinkronisasi otomatis nilai 6 semester, kehadiran, dan ekstrakurikuler — hanya untuk siswa yang datanya sudah terverifikasi."
            />
            <Feature
              icon={<CheckCircle2 className="w-6 h-6" />}
              title="Verifikasi Tata Usaha"
              desc="Admin TU validasi setiap dokumen dengan tombol Validasi & Verifikasi atau Minta Perbaikan disertai catatan."
            />
            <Feature
              icon={<FileText className="w-6 h-6" />}
              title="Cetak Buku Induk PDF"
              desc="Hasilkan PDF 7+ halaman format resmi Kurikulum Merdeka: Biodata, Nilai Semester 1-6, Ekstrakurikuler, Kehadiran."
            />
            <Feature
              icon={<Award className="w-6 h-6" />}
              title="Keterangan Kelulusan"
              desc="Tandai status LULUS atau TIDAK LULUS di halaman akhir Buku Induk dengan nomor SK, tanggal, dan nomor ijazah resmi."
            />
            <Feature
              icon={<BookOpenCheck className="w-6 h-6" />}
              title="Standar Merdeka Belajar"
              desc="Formulir mengikuti standar Kemendikbudristek: Data Pribadi, Orang Tua, Wali, Pendidikan Sebelumnya."
            />
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-slate-500">
        SMP Negeri 1 Penengahan • Kabupaten Lampung Selatan © {new Date().getFullYear()}
      </footer>
    </div>
  );
}

const Stat = ({ number, label }) => (
  <div>
    <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-display">{number}</div>
    <div className="text-xs text-slate-500 mt-1 font-medium">{label}</div>
  </div>
);

const Feature = ({ icon, title, desc }) => (
  <div className="p-6 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
    <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 grid place-items-center mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2 font-display">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);
